import type { ReactNode } from 'react';

import AdminPagination from '../admin/AdminPagination';
import { useAdminTablePagination } from '../../hooks/useAdminTablePagination';

type ApprenantPaginatedListProps<T> = {
  items: T[];
  emptyLabel: string;
  listClassName?: string;
  paginationClassName?: string;
  renderItem: (item: T, index: number) => ReactNode;
};

export function ApprenantPaginatedList<T>({
  items,
  emptyLabel,
  listClassName = 'list-none space-y-0 p-0',
  paginationClassName = 'border-t border-outline-variant/15 px-1 pt-3',
  renderItem,
}: ApprenantPaginatedListProps<T>) {
  const { paginatedItems, currentPage, totalPages, onPageChange, summary } = useAdminTablePagination(items);

  if (items.length === 0) {
    return <p className="text-sm text-on-surface-variant">{emptyLabel}</p>;
  }

  return (
    <>
      <ul className={listClassName}>{paginatedItems.map((item, index) => renderItem(item, index))}</ul>
      <AdminPagination
        className={paginationClassName}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        summary={summary}
      />
    </>
  );
}

type TableColumn = { header: string; className?: string };

type ApprenantPaginatedTableProps<T> = {
  items: T[];
  emptyLabel: string;
  columns: TableColumn[];
  tableClassName?: string;
  paginationClassName?: string;
  renderRow: (item: T, index: number) => ReactNode;
};

export function ApprenantPaginatedTable<T>({
  items,
  emptyLabel,
  columns,
  tableClassName = 'min-w-full text-sm',
  paginationClassName = 'border-t border-outline-variant/15 px-1 pt-3',
  renderRow,
}: ApprenantPaginatedTableProps<T>) {
  const { paginatedItems, currentPage, totalPages, onPageChange, summary } = useAdminTablePagination(items);

  if (items.length === 0) {
    return <p className="text-sm text-on-surface-variant">{emptyLabel}</p>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className={tableClassName}>
          <thead className="text-xs uppercase text-on-surface-variant">
            <tr>
              {columns.map((col) => (
                <th key={col.header} className={`py-2 ${col.className ?? 'text-left'}`.trim()}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{paginatedItems.map((item, index) => renderRow(item, index))}</tbody>
        </table>
      </div>
      <AdminPagination
        className={paginationClassName}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        summary={summary}
      />
    </>
  );
}
